import { addDays, addMonths, addWeeks } from "date-fns";

import type { DurationUnit } from "@/components/ui/duration-input";

import { clinicClockFor, dayMarker, pickedDay } from "@/lib/clinic-clock";
import { formatDate } from "@/lib/format-data";

import { type Rebooking, RebookingIntervalUnit } from "@/types/forms/question";

/** Four weeks is the common aesthetics follow-up. */
export const REBOOKING_FALLBACK_INTERVAL = 4;
/** Mirrors _MAX_INTERVAL_VALUE in decoda/services/rebooking.py, which drops
 *  anything larger rather than rendering it. */
export const REBOOKING_MAX_INTERVAL = 520;
export const REBOOKING_FALLBACK_UNIT = RebookingIntervalUnit.WEEK;
/** Mirrors _MAX_ADJUST_MINUTES in decoda/services/rebooking.py: the most a
 *  follow-up can be lengthened or shortened by. */
export const REBOOKING_MAX_ADJUST_MINUTES = 240;
/** The calendar books in 5-minute slots. */
export const REBOOKING_ADJUST_MINUTES_STEP = 5;
/** The shortest a shortened service is booked at, so "-60" on a 30-minute
 *  service still books a slot rather than nothing. */
export const REBOOKING_MIN_BOOKED_MINUTES = 5;

/** A stored adjustMinutes the backend would keep, else undefined. */
export function readAdjustMinutes(value: unknown): number | undefined {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value !== 0 &&
    Math.abs(value) <= REBOOKING_MAX_ADJUST_MINUTES
    ? value
    : undefined;
}

/** "+30 min" or "−15 min", read next to the interval: "in 6 months · +30 min". */
export function adjustTimeLabel(adjustMinutes: number): string {
  return `${adjustMinutes > 0 ? "+" : "\u2212"}${Math.abs(adjustMinutes)} min`;
}

/** A service's booked length once a follow-up's adjustment is applied. A
 *  shortened service never drops below a bookable slot, and a service with no
 *  length of its own (an add-on container) is never shortened into one. */
export function adjustedDuration(
  defaultMinutes: number,
  adjustMinutes: number | undefined,
): number {
  if (!adjustMinutes) return defaultMinutes;
  return Math.max(
    defaultMinutes + adjustMinutes,
    Math.min(defaultMinutes, REBOOKING_MIN_BOOKED_MINUTES),
  );
}

/** The `serviceAdjustMinutes` booking param: "svc_a:30,svc_b:-15". The
 *  appointment form applies each to its own service's segment. */
export function encodeServiceAdjustMinutes(
  followUps: { serviceId: string; adjustMinutes?: number | null }[],
): string {
  return followUps
    .flatMap(({ serviceId, adjustMinutes }) => {
      const minutes = readAdjustMinutes(adjustMinutes);
      return minutes ? [`${serviceId}:${minutes}`] : [];
    })
    .join(",");
}

export function decodeServiceAdjustMinutes(
  param: string | null,
): Map<string, number> {
  const byService = new Map<string, number>();
  for (const pair of param?.split(",") ?? []) {
    const [serviceId, raw, ...rest] = pair.split(":");
    if (rest.length) continue;
    // Plain digits only: Number() would also read "0x1E" or "3e1" as 30.
    const minutes = /^-?\d+$/.test(raw ?? "")
      ? readAdjustMinutes(Number(raw))
      : undefined;
    if (serviceId && minutes) byService.set(serviceId, minutes);
  }
  return byService;
}

/** The author's default interval, read off a REBOOKING question's data.
 *
 * Question data is stored as raw JSON by the backend's schema validation, which
 * dumps snake_case field names, while the template read path serializes the
 * same schema with its camelCase aliases. Accept either so the default the
 * author set reaches the picker on both paths.
 */
export function readRebookingDefaults(data: Rebooking | undefined | null): {
  intervalValue: number;
  intervalUnit: RebookingIntervalUnit;
} {
  const raw = (data ?? {}) as Record<string, unknown>;
  const value = raw.defaultIntervalValue ?? raw.default_interval_value;
  const unit = raw.defaultIntervalUnit ?? raw.default_interval_unit;
  return {
    intervalValue:
      typeof value === "number" && value > 0
        ? value
        : REBOOKING_FALLBACK_INTERVAL,
    intervalUnit: Object.values(RebookingIntervalUnit).includes(
      unit as RebookingIntervalUnit,
    )
      ? (unit as RebookingIntervalUnit)
      : REBOOKING_FALLBACK_UNIT,
  };
}

/** The units a follow-up interval is written in. Hours and minutes are never
 *  a sensible rebooking window, so the shared picker is narrowed to these. */
export const REBOOKING_DURATION_UNITS: DurationUnit[] = [
  "days",
  "weeks",
  "months",
];

/** Mirrors DurationInput's own constants (a month is 30 days there). */
const MINUTES_PER_UNIT: Record<RebookingIntervalUnit, number> = {
  [RebookingIntervalUnit.DAY]: 1440,
  [RebookingIntervalUnit.WEEK]: 10080,
  [RebookingIntervalUnit.MONTH]: 43200,
};

/** {6, WEEK} -> 60480. DurationInput speaks minutes; the note answer and the
 *  label the front desk reads stay in value+unit, so convert at the boundary. */
export function intervalToMinutes(
  intervalValue: number,
  intervalUnit: RebookingIntervalUnit,
): number {
  return intervalValue * MINUTES_PER_UNIT[intervalUnit];
}

const DURATION_UNITS: Record<RebookingIntervalUnit, DurationUnit> = {
  [RebookingIntervalUnit.DAY]: "days",
  [RebookingIntervalUnit.WEEK]: "weeks",
  [RebookingIntervalUnit.MONTH]: "months",
};

/** WEEK -> "weeks", the unit DurationInput is pinned to. */
export function toDurationUnit(unit: RebookingIntervalUnit): DurationUnit {
  return DURATION_UNITS[unit];
}

/** DurationInput's minutes, read back in the unit the provider chose — never
 *  a unit derived from the minutes, which is how 6 weeks became 1.4 months.
 *  The note stores whole counts the backend keeps (1–520). */
export function durationToInterval(
  minutes: number,
  unit: DurationUnit,
): { intervalValue: number; intervalUnit: RebookingIntervalUnit } {
  const intervalUnit =
    (Object.keys(DURATION_UNITS) as RebookingIntervalUnit[]).find(
      (key) => DURATION_UNITS[key] === unit,
    ) ?? REBOOKING_FALLBACK_UNIT;
  const count = Math.round(minutes / MINUTES_PER_UNIT[intervalUnit]);
  return {
    intervalValue: Math.min(REBOOKING_MAX_INTERVAL, Math.max(1, count)),
    intervalUnit,
  };
}

const ADD_INTERVAL: Record<
  RebookingIntervalUnit,
  (date: Date, amount: number) => Date
> = {
  [RebookingIntervalUnit.DAY]: addDays,
  [RebookingIntervalUnit.WEEK]: addWeeks,
  [RebookingIntervalUnit.MONTH]: addMonths,
};

/** When the patient is due back, counted from the appointment the note is
 *  for and keeping its time of day. Months are calendar months here, not the
 *  30-day months above: this is a day on a calendar, not a duration. */
export function rebookingDueDate(
  appointmentStart: string | Date,
  intervalValue: number,
  intervalUnit: RebookingIntervalUnit,
  /** The appointment location's zone. */
  timeZone: string,
): Date {
  // Counted on the location's calendar, so a DST change in between moves
  // neither the day nor the time of day.
  const clock = clinicClockFor(timeZone);
  const dueDay = ADD_INTERVAL[intervalUnit](
    dayMarker(clock.dayOf(appointmentStart)),
    intervalValue,
  );
  return clock.at(pickedDay(dueDay), clock.wallMinutes(appointmentStart));
}

/** "Dec 22, 2026" — the day the patient is due back. */
export function rebookingDueDay(
  appointmentStart: string | Date,
  intervalValue: number,
  intervalUnit: RebookingIntervalUnit,
  /** The appointment location's zone, the one the front desk books on. */
  timeZone: string,
): string {
  const due = rebookingDueDate(
    appointmentStart,
    intervalValue,
    intervalUnit,
    timeZone,
  );
  return formatDate(due, true, true, undefined, false, timeZone);
}
